import React, { useEffect, useCallback, useRef } from "react";
import { Handle, Position, useReactFlow, useUpdateNodeInternals } from "@xyflow/react";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import SvgIcon from "../../../../components/SvgIcon";
import NodeControl from "../components/node-control.js";
import { useNodeDataUpdate } from "../../hooks/use-node-data-update.js";

const Sequence = ({
  id,
  data,
  selected,
}) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const { updateNodeData, setEdges } = useReactFlow();
  const { handleNodeDataUpdate } = useNodeDataUpdate();

  const titleInputRef = useRef(null);
  const descriptionRef = useRef(null);

  const titleValueRef = useRef(data.title);
  const descriptionValueRef = useRef(data.description);

  useEffect(() => {
    titleValueRef.current = data.title;
  }, [data.title]);

  useEffect(() => {
    descriptionValueRef.current = data.description;
  }, [data.description]);

  const question_add = useCallback(() => {
    const currentQuestions = data.questions || [];

    updateNodeData(id, {
      questions: [
        ...currentQuestions,
        {
          id: "q-" + new Date().getTime(),
          title: "Question " + (currentQuestions.length + 1),
          text: "Enter question here",
          transition: [],
        },
      ],
    });
    updateNodeInternals(id);
  }, [data.questions, updateNodeData, id, updateNodeInternals]);

  const question_update = useCallback(
    (index, field, value) => {
      const currentQuestions = data.questions || [];
      const newQuestions = [...currentQuestions];

      if (newQuestions[index]) {
        newQuestions[index] = {
          ...newQuestions[index],
          [field]: value,
        };
      }

      handleNodeDataUpdate({ id, data: { questions: newQuestions } });
    },
    [id, data.questions, handleNodeDataUpdate]
  );

  const question_del = useCallback(
    (index) => {
      const currentQuestions = data.questions || [];
      const newQuestions = currentQuestions.filter((_, i) => i !== index);
      updateNodeData(id, { questions: newQuestions });
      updateNodeInternals(id);
    },
    [data.questions, updateNodeData, id, updateNodeInternals]
  );

  const transition_add = useCallback(
    (questionIndex) => {
      const currentQuestions = data.questions || [];
      const newQuestions = [...currentQuestions];

      if (newQuestions[questionIndex]) {
        const currentTransitions = newQuestions[questionIndex].transition || [];
        newQuestions[questionIndex].transition = [
          ...currentTransitions,
          {
            handleId: "transition-" + new Date().getTime(),
            text: "Describe answer condition",
          },
        ];
      }

      handleNodeDataUpdate({ id, data: { questions: newQuestions } });
      updateNodeInternals(id);
    },
    [id, data.questions, handleNodeDataUpdate, updateNodeInternals]
  );

  const transition_update = useCallback(
    (questionIndex, transitionIndex, value) => {
      const currentQuestions = data.questions || [];
      const newQuestions = [...currentQuestions];

      if (newQuestions[questionIndex] && newQuestions[questionIndex].transition) {
        newQuestions[questionIndex].transition[transitionIndex].text = value;
      }

      handleNodeDataUpdate({ id, data: { questions: newQuestions } });
    },
    [id, data.questions, handleNodeDataUpdate]
  );

  const transition_del = useCallback(
    (questionIndex, transitionIndex) => {
      const currentQuestions = data.questions || [];
      const newQuestions = [...currentQuestions];

      if (newQuestions[questionIndex]) {
        const toDelHandleId = newQuestions[questionIndex].transition[transitionIndex].handleId;
        newQuestions[questionIndex].transition = newQuestions[questionIndex].transition.filter(
          (_, i) => i !== transitionIndex
        );

        setEdges((edges) =>
          edges.filter(
            (edge) => !(edge.sourceHandle === toDelHandleId && edge.source === id)
          )
        );
      }

      handleNodeDataUpdate({ id, data: { questions: newQuestions } });
      updateNodeInternals(id);
    },
    [id, data.questions, handleNodeDataUpdate, setEdges, updateNodeInternals]
  );

  const handleBlur = useCallback(
    (key, value) => {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\s+/g, " ");
      updateNodeData(id, { [key]: normalizedValue });
    },
    [id, updateNodeData]
  );

  const toggleRandomize = useCallback(() => {
    updateNodeData(id, { randomize: !data.randomize });
  }, [id, data.randomize, updateNodeData]);

  return (
    <div
      className={cn(
        "w-[560px] group inline-flex flex-col items-start justify-start rounded-lg bg-[#F0F9FF] border-2 border-blue-300",
        selected && "outline-2"
      )}
    >
      <Handle className="!top-5" type="target" position={Position.Left} />

      {/* Header */}
      <header className="flex w-full items-center justify-start gap-2 px-3 pb-2 pt-3 border-b border-blue-200">
        <span className="text-blue-600 font-bold">⊕</span>
        <Input
          ref={titleInputRef}
          className="nodrag nowheel font-semibold"
          type="text"
          defaultValue={data.title}
          placeholder="Question Group"
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("title", evt.target.value)}
        />
        <NodeControl id={id} selected={selected} />
      </header>

      <div className="flex w-full flex-col items-start justify-start gap-2 p-3">
        {/* Intro message — spoken once when the group starts, before the first question */}
        <label className="text-xs font-semibold text-gray-600 uppercase w-full">Intro message</label>
        <Textarea
          ref={descriptionRef}
          defaultValue={data.description}
          onMouseDown={(evt) => evt.stopPropagation()}
          onBlur={(evt) => handleBlur("description", evt.target.value)}
          className="nodrag nowheel resize-y bg-white text-sm min-h-24 max-h-56 border border-blue-200"
          placeholder="Read once before the questions, e.g. “I'll read a few names — for each, tell me if your opinion is favorable or unfavorable.”"
        />

        {/* Randomize Toggle */}
        <div className="flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-200 w-full">
          <input
            type="checkbox"
            checked={data.randomize || false}
            onChange={toggleRandomize}
            className="w-4 h-4 accent-blue-600 cursor-pointer"
          />
          <label className="text-xs font-medium text-gray-700 cursor-pointer flex-1">
            {data.randomize ? "✓ Randomize order" : "Randomize order"}
          </label>
          {data.randomize && <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">Random</span>}
        </div>

        {/* Questions Section */}
        <div className="mt-2 flex flex-col items-start justify-start gap-1.5 self-stretch rounded-lg bg-white p-2 border border-blue-200 w-full">
          <div className="inline-flex h-6 w-full items-center justify-between self-stretch pb-2 border-b border-gray-200">
            <span className="h-5 text-xs font-semibold leading-tight text-gray-600 uppercase">
              Questions ({data.questions?.length || 0})
            </span>
            <Button variant="link" onClick={question_add} className="text-xs">
              + Add
            </Button>
          </div>

          <div className="relative flex w-full flex-col gap-3 max-h-[600px] overflow-y-auto">
            {data?.questions?.map((question, qIndex) => (
              <div
                key={question.id}
                className="group/question relative flex flex-col gap-2 rounded-md bg-gray-50 p-2 border-2 border-gray-300"
              >
                {/* Question Header */}
                <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
                  <span className="text-xs font-bold text-gray-600 min-w-fit">Q{qIndex + 1}</span>
                  <Input
                    type="text"
                    defaultValue={question.title}
                    placeholder="Question title"
                    className="nodrag nowheel text-xs h-7 font-medium"
                    onBlur={(evt) => question_update(qIndex, "title", evt.target.value)}
                    onMouseDown={(evt) => evt.stopPropagation()}
                  />
                  <Button
                    variant="link"
                    className="absolute right-1 top-1 cursor-pointer !p-0 h-fit text-red-500 hover:text-red-700"
                    onClick={() => question_del(qIndex)}
                  >
                    <SvgIcon name="material-symbols-light:delete-outline" className="w-4 h-4" />
                  </Button>
                </div>

                {/* Question Text */}
                <Textarea
                  defaultValue={question.text}
                  placeholder="Question text"
                  className="nodrag nowheel resize-y bg-white text-sm min-h-20 max-h-56 border border-gray-200"
                  onBlur={(evt) => question_update(qIndex, "text", evt.target.value)}
                  onMouseDown={(evt) => evt.stopPropagation()}
                />

                {/* Prompt toggle — OFF (gray) = read verbatim, ON (blue) = AI rephrases from instruction */}
                <div className="flex items-center gap-2 px-1">
                  <label className="text-xs text-gray-600 flex-1">Prompt</label>
                  <div
                    onMouseDown={(evt) => evt.stopPropagation()}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      question_update(qIndex, "readExactly", question.readExactly === false);
                    }}
                    className={`nodrag relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                      question.readExactly === false ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        question.readExactly === false ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>

                {/* Require exact answer — ON (blue) = only a listed option is accepted; off-script
                    answers re-ask instead of being force-matched or recorded raw */}
                <div className="flex items-center gap-2 px-1">
                  <label className="text-xs text-gray-600 flex-1">Require exact answer</label>
                  <div
                    onMouseDown={(evt) => evt.stopPropagation()}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      question_update(qIndex, "strictAnswer", question.strictAnswer !== true);
                    }}
                    className={`nodrag relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                      question.strictAnswer === true ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        question.strictAnswer === true ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>

                {/* Transitions/Answers */}
                <div className="flex flex-col gap-1 rounded bg-white p-1.5 border border-blue-200">
                  <div className="flex items-center justify-between pb-1 border-b border-blue-100">
                    <span className="text-xs font-semibold text-blue-700">Answer Options</span>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => transition_add(qIndex)}
                      className="text-xs h-5 p-0"
                    >
                      + Add
                    </Button>
                  </div>

                  <div className="flex flex-col gap-1">
                    {question.transition?.map((trans, tIndex) => (
                      <div
                        key={trans.handleId}
                        className="group/trans relative flex items-start gap-1 rounded bg-blue-50 p-1 border border-blue-100"
                      >
                        <span className="text-xs font-bold text-blue-600 pt-0.5 min-w-fit">A{tIndex + 1}</span>
                        <Textarea
                          defaultValue={trans.text}
                          placeholder="e.g., 'Yes', 'Strongly Agree', 'I don't know'"
                          className="nodrag nowheel resize-y bg-white text-sm min-h-12 max-h-32 flex-1 border border-blue-100"
                          onBlur={(evt) => transition_update(qIndex, tIndex, evt.target.value)}
                          onMouseDown={(evt) => evt.stopPropagation()}
                        />
                        <Button
                          variant="link"
                          className="absolute right-0.5 top-0.5 cursor-pointer !p-0 h-fit text-blue-500 hover:text-red-600 hidden group-hover/trans:block"
                          onClick={() => transition_del(qIndex, tIndex)}
                        >
                          <SvgIcon name="material-symbols-light:delete-outline" className="w-3 h-3" />
                        </Button>
                        <Handle
                          className="!-right-3"
                          type="source"
                          position={Position.Right}
                          id={trans.handleId}
                        />
                      </div>
                    ))}
                  </div>

                  {(!question.transition || question.transition.length === 0) && (
                    <div className="text-xs text-gray-400 text-center py-2">
                      No answer options yet
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {(!data.questions || data.questions.length === 0) && (
            <div className="w-full text-center py-4 text-gray-400 text-xs">
              No questions yet. Click "+ Add" to start.
            </div>
          )}
        </div>
      </div>

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

export default React.memo(Sequence, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.data === nextProps.data
  );
});
